import React, { useMemo, useEffect, useState } from 'react';
import { AdminService, AdminSubscription } from '../../services/adminService';
import { useAdminTheme } from '../../contexts/AdminThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import { billingService } from '../../services/billingService';
import type { Invoice } from '../../services/billingService';

type AdminInvoice = Invoice & {
  userId?: string;
  plan?: string;
  billingPeriod?: string;
  paymentId?: string;
};

const AdminRevenuePage: React.FC = () => {
  const { isDark } = useAdminTheme();
  const { showError } = useNotification();
  const [isLoading, setIsLoading] = useState(true);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [subscriptions, setSubscriptions] = useState<AdminSubscription[]>([]);
  const [invoices, setInvoices] = useState<AdminInvoice[]>([]);
  const [filterDateRange, setFilterDateRange] = useState<'today' | '7days' | '30days' | 'all'>('all');

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        setLoadingInvoices(true);
        const [subs, allInvoices] = await Promise.all([
          AdminService.getAllSubscriptions(),
          billingService.getAllInvoices(5000),
        ]);
        setSubscriptions(subs);
        setInvoices(allInvoices);
      } catch (error) {
        console.error('Error loading revenue page data:', error);
        showError('Failed to load revenue data');
      } finally {
        setIsLoading(false);
        setLoadingInvoices(false);
      }
    };
    load();
  }, [showError]);

  const formatCurrency = (amount: number, currency: string = 'USD') =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);

  const formatDate = (date: Date) =>
    new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);

  const dateLowerBound = useMemo(() => {
    const now = new Date();
    if (filterDateRange === 'today') return new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    if (filterDateRange === '7days') return now.getTime() - 7 * 24 * 60 * 60 * 1000;
    if (filterDateRange === '30days') return now.getTime() - 30 * 24 * 60 * 60 * 1000;
    return 0;
  }, [filterDateRange]);

  const filteredInvoices = useMemo(
    () => invoices.filter((inv) => (inv.created || 0) * 1000 >= dateLowerBound),
    [invoices, dateLowerBound]
  );

  const paidInvoices = useMemo(
    () => filteredInvoices.filter((inv) => String(inv.status || '').toLowerCase() === 'paid'),
    [filteredInvoices]
  );
  const failedInvoices = useMemo(
    () => filteredInvoices.filter((inv) => String(inv.status || '').toLowerCase() === 'failed'),
    [filteredInvoices]
  );
  const refundedInvoices = useMemo(
    () => filteredInvoices.filter((inv) => String(inv.status || '').toLowerCase() === 'refunded'),
    [filteredInvoices]
  );

  const totalRevenue = useMemo(
    () => paidInvoices.reduce((sum, inv) => sum + (inv.amount_paid || 0) / 100, 0),
    [paidInvoices]
  );

  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
  const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1).getTime();

  const revenueThisMonth = useMemo(
    () =>
      invoices
        .filter((inv) => String(inv.status || '').toLowerCase() === 'paid' && (inv.created || 0) * 1000 >= monthStart)
        .reduce((sum, inv) => sum + (inv.amount_paid || 0) / 100, 0),
    [invoices, monthStart]
  );
  const revenueLastMonth = useMemo(
    () =>
      invoices
        .filter((inv) => {
          const t = (inv.created || 0) * 1000;
          return String(inv.status || '').toLowerCase() === 'paid' && t >= lastMonthStart && t < monthStart;
        })
        .reduce((sum, inv) => sum + (inv.amount_paid || 0) / 100, 0),
    [invoices, lastMonthStart, monthStart]
  );
  const momGrowth = revenueLastMonth > 0 ? ((revenueThisMonth - revenueLastMonth) / revenueLastMonth) * 100 : 0;

  const paidUsers = useMemo(
    () => new Set(paidInvoices.map((inv) => String(inv.userId || inv.customer || '').trim()).filter(Boolean)).size,
    [paidInvoices]
  );
  const arpu = paidUsers > 0 ? totalRevenue / paidUsers : 0;

  const revenueByPlan = useMemo(() => {
    const out: Record<string, number> = {};
    for (const inv of paidInvoices) {
      const key = String(inv.plan || 'unknown').toLowerCase();
      out[key] = (out[key] || 0) + (inv.amount_paid || 0) / 100;
    }
    return out;
  }, [paidInvoices]);

  const revenueByProvider = useMemo(() => {
    const out: Record<string, number> = {};
    for (const inv of paidInvoices) {
      const pid = String(inv.paymentId || '').toLowerCase();
      let provider = 'unknown';
      if (pid.startsWith('dodo_')) provider = 'dodo';
      else if (pid.startsWith('pi_')) provider = 'stripe';
      else if (pid.startsWith('pay_')) provider = 'razorpay';
      out[provider] = (out[provider] || 0) + (inv.amount_paid || 0) / 100;
    }
    return out;
  }, [paidInvoices]);

  const revenueByBillingCycle = useMemo(() => {
    const out: Record<string, number> = {};
    for (const inv of paidInvoices) {
      const cycle = String(inv.billingPeriod || 'unknown').toLowerCase();
      out[cycle] = (out[cycle] || 0) + (inv.amount_paid || 0) / 100;
    }
    return out;
  }, [paidInvoices]);

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600" />
      </div>
    );
  }

  return (
    <div className={`p-6 space-y-8 ${isDark ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Revenue Overview</h2>
          <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Only invoice-backed values are shown.
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={filterDateRange}
            onChange={(e) => setFilterDateRange(e.target.value as 'today' | '7days' | '30days' | 'all')}
            className={`text-sm rounded-lg px-3 py-2 border ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
          >
            <option value="today">Today</option>
            <option value="7days">Last 7 Days</option>
            <option value="30days">Last 30 Days</option>
            <option value="all">All Time</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard title="Total Revenue" value={formatCurrency(totalRevenue)} trend={momGrowth} trendLabel="MoM" isDark={isDark} />
        <MetricCard
          title="Revenue This Month"
          value={formatCurrency(revenueThisMonth)}
          subValue={`Last month: ${formatCurrency(revenueLastMonth)}`}
          isDark={isDark}
        />
        <MetricCard
          title="Paid Invoices"
          value={paidInvoices.length.toString()}
          subValue={`Failed: ${failedInvoices.length} • Refunded: ${refundedInvoices.length}`}
          isDark={isDark}
        />
        <MetricCard title="ARPU" value={formatCurrency(arpu)} subValue={`Paid users: ${paidUsers}`} isDark={isDark} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className={`p-6 rounded-xl shadow-sm border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <h3 className="text-lg font-semibold mb-4">Revenue by Plan</h3>
          <div className="space-y-4">
            <ProgressBar label="Starter" value={revenueByPlan.starter || 0} total={totalRevenue} color="bg-blue-500" format={formatCurrency} isDark={isDark} />
            <ProgressBar label="Pro" value={revenueByPlan.pro || 0} total={totalRevenue} color="bg-purple-500" format={formatCurrency} isDark={isDark} />
            <ProgressBar label="Business" value={revenueByPlan.business || 0} total={totalRevenue} color="bg-orange-500" format={formatCurrency} isDark={isDark} />
            <ProgressBar
              label="Enterprise"
              value={revenueByPlan.enterprise || 0}
              total={totalRevenue}
              color="bg-cyan-500"
              format={formatCurrency}
              isDark={isDark}
            />
            <ProgressBar label="Unknown" value={revenueByPlan.unknown || 0} total={totalRevenue} color="bg-gray-500" format={formatCurrency} isDark={isDark} />
          </div>
        </div>

        <div className={`p-6 rounded-xl shadow-sm border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <h3 className="text-lg font-semibold mb-4">Revenue Breakdown</h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between"><span>Dodo</span><span className="font-medium">{formatCurrency(revenueByProvider.dodo || 0)}</span></div>
            <div className="flex justify-between"><span>Stripe</span><span className="font-medium">{formatCurrency(revenueByProvider.stripe || 0)}</span></div>
            <div className="flex justify-between"><span>Razorpay</span><span className="font-medium">{formatCurrency(revenueByProvider.razorpay || 0)}</span></div>
            <div className="flex justify-between"><span>Unknown Provider</span><span className="font-medium">{formatCurrency(revenueByProvider.unknown || 0)}</span></div>
            <div className="pt-3 border-t border-gray-200 dark:border-gray-700" />
            <div className="flex justify-between"><span>Monthly Billing</span><span className="font-medium">{formatCurrency(revenueByBillingCycle.monthly || 0)}</span></div>
            <div className="flex justify-between"><span>Annual Billing</span><span className="font-medium">{formatCurrency(revenueByBillingCycle.annual || revenueByBillingCycle.yearly || 0)}</span></div>
            <div className="flex justify-between"><span>Unknown Cycle</span><span className="font-medium">{formatCurrency(revenueByBillingCycle.unknown || 0)}</span></div>
          </div>
        </div>
      </div>

      <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h3 className="text-lg font-semibold">Active Paid Subscriptions</h3>
          <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            {subscriptions.filter((s) => s.status === 'active').length} active
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className={`text-xs uppercase ${isDark ? 'bg-gray-900/50 text-gray-400' : 'bg-gray-50 text-gray-500'}`}>
              <tr>
                <th className="px-6 py-3">User</th>
                <th className="px-6 py-3">Plan</th>
                <th className="px-6 py-3">Interval</th>
                <th className="px-6 py-3">Created</th>
                <th className="px-6 py-3">Status</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
              {subscriptions.length > 0 ? (
                subscriptions.slice(0, 15).map((sub) => (
                  <tr key={sub.id} className={isDark ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'}>
                    <td className="px-6 py-4 font-medium">{sub.userEmail || 'Unknown User'}</td>
                    <td className="px-6 py-4 capitalize">{sub.planId || 'Unknown'}</td>
                    <td className="px-6 py-4 capitalize">{sub.interval}</td>
                    <td className={`px-6 py-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {sub.createdAt ? formatDate(sub.createdAt.toDate ? sub.createdAt.toDate() : new Date((sub.createdAt as any).seconds * 1000)) : '-'}
                    </td>
                    <td className="px-6 py-4">
                      <StatusBadge status={sub.status} />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                    No active subscriptions found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold">All Invoices</h3>
            <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Records shown: {filteredInvoices.length} {loadingInvoices ? '(loading...)' : ''}
            </p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className={`text-xs uppercase ${isDark ? 'bg-gray-900/50 text-gray-400' : 'bg-gray-50 text-gray-500'}`}>
              <tr>
                <th className="px-6 py-3">Invoice ID</th>
                <th className="px-6 py-3">User ID</th>
                <th className="px-6 py-3">Plan</th>
                <th className="px-6 py-3">Amount</th>
                <th className="px-6 py-3">Currency</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3">Date</th>
                <th className="px-6 py-3">Payment ID</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
              {filteredInvoices.length > 0 ? (
                filteredInvoices.map((inv) => (
                  <tr key={inv.id} className={isDark ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'}>
                    <td className="px-6 py-4 font-mono text-xs">{inv.id}</td>
                    <td className="px-6 py-4 font-mono text-xs" title={inv.userId || inv.customer}>
                      {(inv.userId || inv.customer || '').slice(0, 12)}...
                    </td>
                    <td className="px-6 py-4 capitalize">{inv.plan || 'N/A'}</td>
                    <td className="px-6 py-4 font-medium">{formatCurrency((inv.amount_paid || 0) / 100, inv.currency || 'USD')}</td>
                    <td className="px-6 py-4 uppercase text-xs">{inv.currency || 'usd'}</td>
                    <td className="px-6 py-4"><StatusBadge status={inv.status} /></td>
                    <td className={`px-6 py-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{formatDate(new Date(inv.created * 1000))}</td>
                    <td className="px-6 py-4 font-mono text-xs" title={inv.paymentId || ''}>
                      {inv.paymentId ? `${inv.paymentId.slice(0, 16)}...` : '-'}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="px-6 py-10 text-center text-gray-500">
                    No invoices found for this date filter
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const MetricCard: React.FC<{
  title: string;
  value: string;
  subValue?: string;
  trend?: number;
  trendLabel?: string;
  isDark: boolean;
}> = ({ title, value, subValue, trend, trendLabel, isDark }) => (
  <div className={`p-6 rounded-xl shadow-sm border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
    <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{title}</p>
    <h3 className="text-2xl font-bold mt-2">{value}</h3>
    <div className="mt-4 flex items-center justify-between text-sm">
      {subValue && <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>{subValue}</span>}
      {trend !== undefined && (
        <span className={`font-medium ${trend >= 0 ? 'text-green-500' : 'text-red-500'}`}>
          {trend >= 0 ? '+' : ''}{trend.toFixed(1)}% {trendLabel || ''}
        </span>
      )}
    </div>
  </div>
);

const ProgressBar: React.FC<{
  label: string;
  value: number;
  total: number;
  color: string;
  format: (val: number) => string;
  isDark: boolean;
}> = ({ label, value, total, color, format, isDark }) => {
  const percentage = total > 0 ? (value / total) * 100 : 0;
  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>{label}</span>
        <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>
          {format(value)} ({percentage.toFixed(1)}%)
        </span>
      </div>
      <div className={`w-full rounded-full h-2 ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
        <div className={`h-2 rounded-full ${color}`} style={{ width: `${percentage}%` }} />
      </div>
    </div>
  );
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const styles = {
    paid: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    refunded: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    cancelled: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  } as const;
  const key = String(status || 'pending').toLowerCase() as keyof typeof styles;
  const style = styles[key] || styles.pending;
  return <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${style}`}>{status}</span>;
};

export default AdminRevenuePage;
